import { useEffect, useState } from 'react';
import { Bell, LogOut, MessageSquare, Plus, Search } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

import { logout } from '../../hooks/use-auth';

const routeLabels: Record<string, string> = {
  '/': 'Início',
  '/dashboard': 'Dashboard',
  '/identities': 'Identidades',
  '/catalog': 'Catálogo',
  '/subscriptions': 'Assinaturas',
  '/charges': 'Cobranças',
  '/fiscal': 'Notas Fiscais',
  '/communications': 'Comunicação',
  '/reports': 'Relatórios',
  '/settings': 'Configurações',
  '/integrations': 'Integrações',
  '/integrations/owltask': 'OwlTask',
  '/integrations/reservei': 'Reservei',
  '/integrations/vizzy': 'Vizzy AI',
};

interface TopbarProps {
  sseConnected?: boolean;
}

export function Topbar({ sseConnected = false }: TopbarProps): JSX.Element {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [searchFocused, setSearchFocused] = useState(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        const input = document.getElementById('cc-search') as HTMLInputElement | null;
        input?.focus();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const crumb = routeLabels[pathname] ?? pathname.replace(/^\//, '');

  return (
    <header className="flex h-16 items-center gap-[14px] border-b border-line px-[22px]">
      <div className="text-[13px] text-muted">
        Workspace / <b className="font-medium text-ink">{crumb}</b>
      </div>

      <div className="flex-1" />

      <span
        title={
          sseConnected ? 'Conectado em tempo real' : 'Sem conexão real-time (fallback polling)'
        }
        className={`inline-flex items-center gap-1 rounded-[6px] border px-2 py-[2px] text-[10px] font-medium uppercase tracking-wider ${
          sseConnected
            ? 'border-good/40 bg-good/10 text-good'
            : 'border-muted/30 bg-panel-2 text-muted'
        }`}
      >
        <span
          className={`h-[6px] w-[6px] rounded-full ${sseConnected ? 'bg-good' : 'bg-muted'}`}
          aria-hidden
        />
        {sseConnected ? 'ao vivo' : 'offline'}
      </span>

      <div
        className={`flex w-[280px] items-center gap-2 rounded-[10px] border bg-panel px-3 py-[7px] transition-colors ${
          searchFocused ? 'border-line-2' : 'border-line'
        }`}
      >
        <Search size={14} strokeWidth={2} className="text-muted-2" />
        <input
          id="cc-search"
          type="text"
          placeholder="Buscar projetos, arquivos, pessoas…"
          onFocus={() => setSearchFocused(true)}
          onBlur={() => setSearchFocused(false)}
          className="flex-1 border-0 bg-transparent text-[13px] text-ink outline-none placeholder:text-muted-2"
        />
        <span className="rounded border border-line px-[5px] py-[1px] font-mono text-[10px] text-muted-2">
          ⌘K
        </span>
      </div>

      <button
        type="button"
        onClick={() => navigate('/charges/new')}
        className="inline-flex items-center gap-2 rounded-[10px] border border-line px-3 py-[7px] text-[12px] font-medium text-ink-2 transition-colors hover:border-line-2 hover:bg-panel-2"
      >
        <Plus size={14} strokeWidth={2} className="text-muted" />
        <span>Nova cobrança</span>
      </button>

      <button
        type="button"
        aria-label="Notificações"
        className="relative flex h-8 w-8 items-center justify-center rounded-[6px] text-muted transition-colors hover:bg-panel-2 hover:text-ink"
      >
        <Bell size={15} strokeWidth={2} />
        <span className="absolute right-[7px] top-[6px] h-[6px] w-[6px] rounded-full border-2 border-page bg-ink-2" />
      </button>

      <button
        type="button"
        aria-label="Mensagens"
        className="flex h-8 w-8 items-center justify-center rounded-[6px] text-muted transition-colors hover:bg-panel-2 hover:text-ink"
      >
        <MessageSquare size={15} strokeWidth={2} />
      </button>

      <button
        type="button"
        onClick={() => void logout()}
        aria-label="Sair"
        className="flex h-8 w-8 items-center justify-center rounded-[6px] text-muted transition-colors hover:bg-panel-2 hover:text-ink"
      >
        <LogOut size={15} strokeWidth={2} />
      </button>
    </header>
  );
}
